import { useCallback, useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { pickedClientState, reportCutoffState, selectedFundState } from '../state.js';
import { useApi } from '../hooks.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.js';
import { readStoredClient, resolvePickedClient, storeClient } from '../clientChoice.js';
import type { ClientInfo } from '@agio/core';

/**
 * Switch the client the whole app is looking at. The header picker and anything
 * that has to follow a client (a resumed upload link, say) go through this, so
 * none of them carries the previous client's report month or fund along.
 */
export function useSwitchClient() {
  const setPicked = useSetRecoilState(pickedClientState);
  const setCutoff = useSetRecoilState(reportCutoffState);
  const setFund = useSetRecoilState(selectedFundState);
  return useCallback((id: string | null) => {
    setPicked(id);
    if (id) storeClient(window.localStorage, id); // so a reload comes back to it
    setCutoff(null); // never carry a past-client cutoff onto the newly picked one
    // Nor a fund: ids are bare fund names, so the previous client's pick can
    // resolve to a different client's fund of the same name, or to nothing.
    setFund(null);
  }, [setPicked, setCutoff, setFund]);
}

/** Uber-only account switch. Starts on the client this browser last picked
 * (if it is still listed), otherwise the FIRST client so the portal is never
 * empty; viewers are scoped by RLS and never see this. */
export function ClientPicker() {
  const picked = useRecoilValue(pickedClientState);
  const switchClient = useSwitchClient();
  const { data: clients } = useApi<ClientInfo[]>('/clients');

  // Choose a client when nothing is chosen (a fresh load: the remembered one),
  // and drop a choice that has stopped existing — hiding the selected client
  // used to leave it selected, so the portal kept showing the client the admin
  // had just removed.
  useEffect(() => {
    if (!clients || clients.length === 0) return;
    const ids = clients.map((c) => c.client_id);
    const next = resolvePickedClient(ids, picked, readStoredClient(window.localStorage));
    if (next !== picked) switchClient(next);
  }, [clients, picked, switchClient]);

  return (
    <Select
      dir="rtl"
      value={picked ?? ''}
      onValueChange={(v) => switchClient(v || null)}
    >
      <SelectTrigger className="min-w-36 border-0 bg-transparent">
          <SelectValue placeholder="בחר לקוח…" />
        </SelectTrigger>
        <SelectContent>
          {(clients ?? []).map((c) => (
            // textValue: typing a name jumps to it. Without it Radix matches on
            // the row's whole text, which starts with the avatar's initial
            // ("ממיטב…"), so anything past the first letter never matched.
            <SelectItem key={c.client_id} value={c.client_id} textValue={c.display_name_he}>
              <span className="flex items-center gap-2">
                <ClientAvatar name={c.display_name_he} logoUrl={c.logo_url} size={20} />
                {c.display_name_he}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
    </Select>
  );
}

/** Client logo (external logo_url when set via the admin action) or an initials avatar. */
export function ClientAvatar({ name, logoUrl, size = 28 }: { name: string; logoUrl: string | null; size?: number }) {
  if (logoUrl) {
    return <img src={logoUrl} alt={name} width={size} height={size} className="rounded-full bg-white/90 object-contain p-0.5" />;
  }
  return (
    <span
      className="inline-flex items-center justify-center rounded-full bg-emerald-600/20 font-bold text-emerald-300"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {name.trim().slice(0, 1)}
    </span>
  );
}
